import { useEffect, useRef, useState } from 'react'
import { ErrorBanner, useAsync } from '../../components/Async'
import { PageTitle } from '../../components/PageTitle'
import { ApiError, branding as brandingApi, session } from '../../lib/api'
import {
  BRAND_SWATCHES,
  DEFAULT_ACCENT,
  MAXIMUM_FOOTER_LENGTH,
  applyBrand,
  brandQuery,
  deploymentZone,
  domainProblem,
  footerProblem,
  markProblem,
  swatchDrift
} from '../../lib/branding'

/**
 * What this organization looks like: to the people who work in it, and to anyone reading a report it
 * produced.
 *
 * Per organization, unlike the palette on Settings. An operator's choice of Broadsheet or Nocturne is
 * theirs; the mark and the color belong to the company they work for, which is why this screen is
 * behind the owner and administrator roles and Settings is behind nobody.
 *
 * Six swatches rather than a color picker, and that is the whole design rather than a simplification
 * of it. Every accent the console paints is mixed from the chosen one in CSS toward the palette's own
 * ink, so the promise is that a reseller cannot make the console illegible - and that promise is only
 * as good as the set it was measured over. `styles.test.ts` measures these six in all four palettes.
 *
 * The preview is the page itself. There is no mock screenshot here because there does not need to be:
 * choosing a swatch writes `--brand` on the document, so the rail, the buttons, the rules and this
 * screen's own controls all change at once. A preview that showed a small rectangle instead would be
 * a less honest answer to "what will this look like" than the console already is.
 */
export function BrandingPage() {
  const settings = useAsync(() => brandingApi.settings(), [])
  const swatches = useAsync(() => brandingApi.swatches(), [])

  const [accent, setAccent] = useState<string | null>(null)
  const [domain, setDomain] = useState('')
  const [footer, setFooter] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  // Set when a stored full host was reduced to its first label on the way into the field. See the
  // effect below for why that happens at all.
  const [reduced, setReduced] = useState<string | null>(null)
  const [markVersion, setMarkVersion] = useState(0)
  const [hasMark, setHasMark] = useState(false)
  const markInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!settings.value) return
    setAccent(settings.value.configured ? settings.value.brandAccent : null)

    /*
      A sign-in domain stored as a whole host is shown as the subdomain it would become.

      Migration 0182 rewrote these, but deliberately left behind any row whose first label another
      organization had already claimed - rewriting those would have handed one organization's host to
      another. Such a row is still a full host, which this screen now refuses, and refusing it
      disabled Save for the whole card: branding is one atomic write, so an organization in that state
      could not change its color or its report footer either, on account of a field neither of them
      is about.

      Reducing it here applies the migration's own rule - keep the first label - to the rows the
      migration would not touch, and says so in a sentence rather than quietly. If the label is one
      somebody else holds, the server refuses the save by name, which is the same answer it has always
      given for a host two organizations both want.
    */
    const stored = settings.value.signInDomain ?? ''
    const label = stored.split('.')[0] ?? ''
    setDomain(label)
    setReduced(stored.includes('.') ? stored : null)
    setFooter(settings.value.reportFooter ?? '')
    setHasMark(settings.value.hasBrandMark)
  }, [settings.value])

  /**
   * The preview, and the reason it is an effect rather than something the save does.
   *
   * The color is applied the moment it is chosen so an operator can see it before committing, and
   * the cleanup puts back whatever was stored - so navigating away from a choice that was never saved
   * does not leave the console wearing it.
   */
  useEffect(() => {
    applyBrand(document.documentElement, accent)
    return () => {
      applyBrand(document.documentElement, settings.value?.configured ? settings.value.brandAccent : null)
    }
  }, [accent, settings.value])

  const drift = swatches.value ? swatchDrift(swatches.value) : null
  const domainFault = domainProblem(domain)
  // Read off the window rather than configured, so it is true on whatever host this deployment is
  // actually served from without a setting that has to be kept in step with it.
  const zone = deploymentZone(window.location.hostname)
  const footerFault = footerProblem(footer)
  // The mark route is the anonymous one - it takes a slug or a host, because a sign-in page has
  // neither a token nor a tenant scope. This screen has a session, so it names its own organization
  // rather than gaining a second endpoint that would exist only for the preview.
  const markQuery = brandQuery({ slug: session.identity()?.tenantSlug ?? '' })

  async function save() {
    setFailure(null)
    setSaved(null)
    setSaving(true)
    try {
      await brandingApi.save({
        brandAccent: accent ?? DEFAULT_ACCENT,
        signInDomain: domain.trim() || null,
        reportFooter: footer.trim() || null
      })
      setSaved('Saved. Everybody in this organization sees it on their next page load.')
      settings.reload()
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'That could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  async function uploadMark(file: File | null) {
    setFailure(null)
    setSaved(null)

    const problem = markProblem(file)
    if (problem || !file) {
      setFailure(problem)
      return
    }

    try {
      await brandingApi.uploadMark(file)
      setHasMark(true)
      // The mark is served from its own cacheable route, so a fresh upload needs a fresh URL or the
      // browser shows the old one back. The counter is that, and nothing more.
      setMarkVersion((current) => current + 1)
      setSaved('The mark was uploaded. It appears on the sign-in page and on exported reports.')
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'That mark could not be uploaded.')
    } finally {
      if (markInput.current) markInput.current.value = ''
    }
  }

  return (
    <>
      <div className="page-header">
        <div>
          <PageTitle>Branding</PageTitle>
          <p>
            How this organization appears on the sign-in page, in the console, and on the reports it
            exports.
          </p>
        </div>
      </div>

      <ErrorBanner message={settings.error ?? failure} />
      {drift && (
        <div className="banner error" role="alert" data-testid="swatch-drift">
          {drift}
        </div>
      )}
      {saved && (
        <div className="banner" role="status" data-testid="branding-saved">
          {saved}
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <h3 style={{ marginTop: 0 }}>Color</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            Six, rather than a color picker. Every other color in the console is derived from this
            one by mixing it toward the ink of whichever palette somebody is reading in, which is what
            keeps the console legible whichever of these is chosen. A free choice could not promise
            that.
          </p>

          <div className="brand-swatches" role="radiogroup" aria-label="Brand color">
            {BRAND_SWATCHES.map((swatch) => (
              <label
                key={swatch.value}
                className={`brand-swatch${accent === swatch.value ? ' selected' : ''}`}
                data-testid={`swatch-${swatch.value.slice(1)}`}
              >
                <input
                  type="radio"
                  name="brand-accent"
                  value={swatch.value}
                  checked={accent === swatch.value}
                  onChange={() => setAccent(swatch.value)}
                />
                <span className="brand-chip" style={{ background: swatch.value }} aria-hidden="true" />
                <span className="brand-name">{swatch.name}</span>
              </label>
            ))}
          </div>

          {accent === null && (
            <p className="hint" data-testid="brand-unchosen">
              This organization has not chosen a color, so it is shown the deployment default. That is
              a different thing from having chosen the default, and this screen says which.
            </p>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <h3 style={{ marginTop: 0 }}>Mark</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            A PNG or SVG under 64 KB, square. It appears above the sign-in form and in the footer of an
            exported report. It is served without a token, because a sign-in page has nobody signed in.
          </p>

          <div className="brand-mark-row">
            <div className="brand-mark-preview" data-testid="brand-mark-preview">
              {hasMark ? (
                <img src={`${brandingApi.markUrl(markQuery)}&v=${markVersion}`} alt="This organization's mark" />
              ) : (
                <span className="muted">No mark</span>
              )}
            </div>

            <div className="field">
              <label htmlFor="brand-mark">Upload a mark</label>
              <input
                id="brand-mark"
                ref={markInput}
                type="file"
                accept="image/png,image/svg+xml"
                onChange={(event) => void uploadMark(event.target.files?.[0] ?? null)}
              />
              <span className="hint">Uploaded as soon as it is chosen. There is no separate save for it.</span>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <h3 style={{ marginTop: 0 }}>Sign-in domain</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            A subdomain of this console&rsquo;s own address. Reached on it, the sign-in page carries
            your mark before anybody types an organization. One subdomain belongs to one organization.
          </p>

          <div className="field">
            <label htmlFor="sign-in-domain">Subdomain</label>
            {/*
              The rest of the host is shown beside the field rather than typed into it, because it is
              not the organization's to choose. This console can brand a subdomain of the zone it is
              already served on - the deployment answers for that - and it has no way to check that
              anybody controls a host of their own. There is no DNS proof anywhere in this product.
            */}
            <span className="domain-field">
              <input
                id="sign-in-domain"
                value={domain}
                placeholder="acme"
                onChange={(event) => setDomain(event.target.value)}
              />
              {zone && (
                <span className="domain-suffix" data-testid="domain-suffix">
                  .{zone}
                </span>
              )}
            </span>
            {domainFault && <span className="hint error">{domainFault}</span>}
            {reduced && !domainFault && (
              <span className="hint" data-testid="domain-reduced">
                This was {reduced}, a whole host. A sign-in domain is now a subdomain of this
                console&rsquo;s own address, so saving will store {domain.trim().toLowerCase()}.
              </span>
            )}
            {/* Stated rather than left to be inferred from a suffix: the whole host is the thing
                somebody will paste into a browser to check their own work. */}
            {!domainFault && domain.trim() && zone && (
              <span className="hint" data-testid="domain-preview">
                Signs in at {domain.trim().toLowerCase()}.{zone}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <h3 style={{ marginTop: 0 }}>Report footer</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            One line at the bottom of every export. An organization name and a classification is what
            this is for.
          </p>

          <div className="field">
            <label htmlFor="report-footer">Footer</label>
            <input
              id="report-footer"
              value={footer}
              maxLength={MAXIMUM_FOOTER_LENGTH + 1}
              placeholder="Acme Corporation - Information Security - confidential"
              onChange={(event) => setFooter(event.target.value)}
            />
            {footerFault && <span className="hint error">{footerFault}</span>}
          </div>
        </div>
      </div>

      <div className="toolbar">
        <button
          type="button"
          className="primary"
          data-testid="save-branding"
          disabled={saving || domainFault !== null || footerFault !== null}
          onClick={() => void save()}
        >
          {saving ? 'Saving…' : 'Save branding'}
        </button>
      </div>
    </>
  )
}
